/**
 * Android sparse image format (system/core/libsparse) — parse and split so that every
 * piece fits the device's `max-download-size`. Pure functions, no I/O.
 */

export const SPARSE_MAGIC = 0xed26ff3a;
export const SPARSE_HEADER_SIZE = 28;
export const CHUNK_HEADER_SIZE = 12;
export const DEFAULT_BLOCK_SIZE = 4096;

export const CHUNK_RAW = 0xcac1;
export const CHUNK_FILL = 0xcac2;
export const CHUNK_DONT_CARE = 0xcac3;
export const CHUNK_CRC32 = 0xcac4;

export interface SparseHeader {
  majorVersion: number;
  minorVersion: number;
  fileHeaderSize: number;
  chunkHeaderSize: number;
  blockSize: number;
  totalBlocks: number;
  totalChunks: number;
  imageChecksum: number;
}

export interface SparseChunk {
  type: number;
  /** size in blocks */
  blocks: number;
  /** payload bytes (raw data, or the 4-byte fill pattern); empty for DONT_CARE/CRC32 */
  data: Uint8Array;
}

export function isSparse(data: Uint8Array): boolean {
  if (data.byteLength < SPARSE_HEADER_SIZE) return false;
  return new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(0, true) === SPARSE_MAGIC;
}

export function parseSparseHeader(data: Uint8Array): SparseHeader {
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (data.byteLength < SPARSE_HEADER_SIZE || dv.getUint32(0, true) !== SPARSE_MAGIC) {
    throw new Error("not a sparse image");
  }
  const h: SparseHeader = {
    majorVersion: dv.getUint16(4, true),
    minorVersion: dv.getUint16(6, true),
    fileHeaderSize: dv.getUint16(8, true),
    chunkHeaderSize: dv.getUint16(10, true),
    blockSize: dv.getUint32(12, true),
    totalBlocks: dv.getUint32(16, true),
    totalChunks: dv.getUint32(20, true),
    imageChecksum: dv.getUint32(24, true),
  };
  if (h.majorVersion !== 1) throw new Error(`unsupported sparse major version ${h.majorVersion}`);
  if (h.blockSize % 4 !== 0 || h.blockSize === 0) throw new Error(`bad sparse block size ${h.blockSize}`);
  return h;
}

/** Iterate chunks of a sparse image (validates sizes; throws on corruption). */
export function* iterateChunks(data: Uint8Array): Generator<SparseChunk> {
  const h = parseSparseHeader(data);
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let off = h.fileHeaderSize;
  for (let i = 0; i < h.totalChunks; i++) {
    if (off + h.chunkHeaderSize > data.byteLength) throw new Error("truncated sparse chunk header");
    const type = dv.getUint16(off, true);
    const blocks = dv.getUint32(off + 4, true);
    const totalSize = dv.getUint32(off + 8, true);
    const payloadLen = totalSize - h.chunkHeaderSize;
    const payloadOff = off + h.chunkHeaderSize;
    if (payloadOff + payloadLen > data.byteLength) throw new Error("truncated sparse chunk payload");
    switch (type) {
      case CHUNK_RAW:
        if (payloadLen !== blocks * h.blockSize) throw new Error("raw chunk size mismatch");
        break;
      case CHUNK_FILL:
        if (payloadLen !== 4) throw new Error("fill chunk size mismatch");
        break;
      case CHUNK_DONT_CARE:
        if (payloadLen !== 0) throw new Error("dont-care chunk size mismatch");
        break;
      case CHUNK_CRC32:
        if (payloadLen !== 4) throw new Error("crc32 chunk size mismatch");
        break;
      default:
        throw new Error(`unknown sparse chunk type 0x${type.toString(16)}`);
    }
    yield { type, blocks, data: data.subarray(payloadOff, payloadOff + payloadLen) };
    off += totalSize;
  }
}

interface OutChunk {
  type: number;
  blocks: number;
  data: Uint8Array;
}

function chunkBytes(c: OutChunk): number {
  return CHUNK_HEADER_SIZE + c.data.byteLength;
}

function encodeSparse(chunks: OutChunk[], blockSize: number, totalBlocks: number): Uint8Array {
  const size = SPARSE_HEADER_SIZE + chunks.reduce((n, c) => n + chunkBytes(c), 0);
  const out = new Uint8Array(size);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, SPARSE_MAGIC, true);
  dv.setUint16(4, 1, true);
  dv.setUint16(6, 0, true);
  dv.setUint16(8, SPARSE_HEADER_SIZE, true);
  dv.setUint16(10, CHUNK_HEADER_SIZE, true);
  dv.setUint32(12, blockSize, true);
  dv.setUint32(16, totalBlocks, true);
  dv.setUint32(20, chunks.length, true);
  dv.setUint32(24, 0, true);
  let off = SPARSE_HEADER_SIZE;
  for (const c of chunks) {
    dv.setUint16(off, c.type, true);
    dv.setUint16(off + 2, 0, true);
    dv.setUint32(off + 4, c.blocks, true);
    dv.setUint32(off + 8, chunkBytes(c), true);
    out.set(c.data, off + CHUNK_HEADER_SIZE);
    off += chunkBytes(c);
  }
  return out;
}

/**
 * Split an image so that every returned buffer is ≤ maxDownload bytes.
 *
 * - A raw image that already fits is returned unchanged (flashed as a plain image).
 * - A raw image that does not fit is converted into N sparse images; each covers a range
 *   of blocks and marks the blocks before it DONT_CARE, so flashing them in order writes
 *   the whole partition (same strategy as the `fastboot` host tool's resparse).
 * - A sparse image is re-split at chunk/block boundaries. CRC32 chunks are dropped.
 *
 * The last block of a raw image is zero-padded to blockSize (partition images from the
 * build are block-aligned already; padding is only a safety net).
 */
export function splitForDownload(
  data: Uint8Array,
  maxDownload: number,
  blockSize: number = DEFAULT_BLOCK_SIZE,
): Uint8Array[] {
  const minPiece = SPARSE_HEADER_SIZE + 2 * CHUNK_HEADER_SIZE + blockSize;
  if (maxDownload < minPiece) throw new Error(`max-download-size ${maxDownload} too small to split images`);

  if (isSparse(data)) return splitSparse(data, maxDownload);
  if (data.byteLength <= maxDownload) return [data];

  const totalBlocks = Math.ceil(data.byteLength / blockSize);
  const pieces: Uint8Array[] = [];
  let blockOff = 0;
  while (blockOff < totalBlocks) {
    const overhead = SPARSE_HEADER_SIZE + CHUNK_HEADER_SIZE + (blockOff > 0 ? CHUNK_HEADER_SIZE : 0);
    const maxBlocks = Math.floor((maxDownload - overhead) / blockSize);
    const n = Math.min(maxBlocks, totalBlocks - blockOff);
    const start = blockOff * blockSize;
    const end = Math.min(data.byteLength, start + n * blockSize);
    let raw = data.subarray(start, end);
    if (raw.byteLength !== n * blockSize) {
      const padded = new Uint8Array(n * blockSize);
      padded.set(raw);
      raw = padded;
    }
    const chunks: OutChunk[] = [];
    if (blockOff > 0) chunks.push({ type: CHUNK_DONT_CARE, blocks: blockOff, data: new Uint8Array(0) });
    chunks.push({ type: CHUNK_RAW, blocks: n, data: raw });
    pieces.push(encodeSparse(chunks, blockSize, totalBlocks));
    blockOff += n;
  }
  return pieces;
}

function splitSparse(data: Uint8Array, maxDownload: number): Uint8Array[] {
  const h = parseSparseHeader(data);
  const bs = h.blockSize;
  const pieces: Uint8Array[] = [];
  let cur: OutChunk[] = [];
  let curBytes = SPARSE_HEADER_SIZE;
  let curStartBlock = 0; // first block covered by `cur` (after its leading DONT_CARE)
  let blockPos = 0; // absolute block position while walking

  const flush = () => {
    if (cur.length === 0) return;
    const chunks: OutChunk[] = [];
    if (curStartBlock > 0) chunks.push({ type: CHUNK_DONT_CARE, blocks: curStartBlock, data: new Uint8Array(0) });
    chunks.push(...cur);
    pieces.push(encodeSparse(chunks, bs, h.totalBlocks));
    cur = [];
    curBytes = SPARSE_HEADER_SIZE;
  };
  const room = () => maxDownload - curBytes - (cur.length === 0 && blockPos > 0 ? CHUNK_HEADER_SIZE : 0);
  const push = (c: OutChunk) => {
    if (cur.length === 0) curStartBlock = blockPos;
    cur.push(c);
    curBytes += chunkBytes(c);
    blockPos += c.blocks;
  };

  for (const c of iterateChunks(data)) {
    if (c.type === CHUNK_CRC32) continue;
    if (c.type === CHUNK_DONT_CARE) {
      // Skipped blocks cost nothing; just advance (a leading DONT_CARE is synthesised on flush).
      if (cur.length > 0) push({ type: CHUNK_DONT_CARE, blocks: c.blocks, data: new Uint8Array(0) });
      else blockPos += c.blocks;
      continue;
    }
    if (c.type === CHUNK_FILL) {
      if (room() < CHUNK_HEADER_SIZE + 4) flush();
      push({ type: CHUNK_FILL, blocks: c.blocks, data: c.data });
      continue;
    }
    // RAW: split at block boundaries as needed.
    let done = 0;
    while (done < c.blocks) {
      let avail = Math.floor((room() - CHUNK_HEADER_SIZE) / bs);
      if (avail <= 0) {
        flush();
        avail = Math.floor((room() - CHUNK_HEADER_SIZE) / bs);
        if (avail <= 0) throw new Error("max-download-size too small for one block");
      }
      const n = Math.min(avail, c.blocks - done);
      push({ type: CHUNK_RAW, blocks: n, data: c.data.subarray(done * bs, (done + n) * bs) });
      done += n;
    }
  }
  flush();
  return pieces;
}

/** Expand a sparse image into `out` (used by tests and the fake device). */
export function unsparseInto(out: Uint8Array, data: Uint8Array): void {
  const h = parseSparseHeader(data);
  let block = 0;
  for (const c of iterateChunks(data)) {
    const off = block * h.blockSize;
    if (c.type === CHUNK_RAW) out.set(c.data, off);
    else if (c.type === CHUNK_FILL) {
      for (let i = 0; i < c.blocks * h.blockSize; i++) out[off + i] = c.data[i % 4]!;
    }
    if (c.type !== CHUNK_CRC32) block += c.blocks;
  }
}
